// Pure, framework-free helpers that turn the ad-source parameters on an incoming landing-page URL
// into the Adjust deep-link arguments every acquisition CTA uses. SEOENG-38.
//
// The problem this solves: every "Start Trading" / download CTA on the ID landing pages asked the
// backend for an ORGANIC Adjust link (campaign "Download_banner_btn" on the website link token).
// A visitor who arrived from a Google SEM / GDN / YouTube ad therefore installed under "organic"
// in Adjust, and the funded-account event that followed could never be tied back to the channel
// that paid for the click. This module reads the campaign parameters Google puts on the landing
// URL, keeps them for the session, and derives a paid Adjust argument set (campaign / adgroup /
// creative / label / link token) so the install and every later in-app event are attributed to
// the ad. Visitors without campaign parameters keep the organic behaviour untouched.
//
// Parameter contract (what marketing appends to the Google Ads final URL / final URL suffix):
//
//   utm_source    google                      (who: the ad network)
//   utm_medium    cpc | display | video       (which channel: SEM | GDN | YouTube). ValueTrack
//                 {network} codes are accepted too: g/s -> sem, d -> gdn, ytv/yts/vp -> youtube.
//   utm_campaign  <readable campaign name>    (ValueTrack {campaignid} works, names read better)
//   utm_content   {adgroupid}_{creative}      (ad-level detail, becomes the Adjust adgroup)
//   utm_term      {keyword} | {placement}     (SEM keyword or GDN/YouTube placement -> creative)
//   gclid / gbraid / wbraid / dclid            (Google click identifiers, appended by auto-tagging;
//                                              preserved verbatim, never rewritten)
//
// Everything here is side-effect free except the sessionStorage helpers, and nothing imports
// React or Gatsby, so the whole contract is covered by campaign-attribution.test.mjs (node --test).

export const CHANNEL = Object.freeze({
  SEM: 'sem',
  GDN: 'gdn',
  YOUTUBE: 'youtube',
  // A Google click id with no usable utm_medium: paid Google traffic whose channel we cannot name.
  GOOGLE: 'google',
  // utm_source present but not a Google channel we recognise (e.g. a Meta or TikTok campaign).
  PAID: 'paid'
});

export const UTM_KEYS = Object.freeze([
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term'
]);

// gclid: classic Google click id. gbraid / wbraid: the iOS 14.5+ privacy-safe variants Google
// sends instead of gclid for web-to-app (gbraid) and app-to-web (wbraid). dclid: Display & Video
// 360 / Campaign Manager click id.
export const CLICK_ID_KEYS = Object.freeze(['gclid', 'gbraid', 'wbraid', 'dclid']);

// Google's newer auto-tag parameters. Not click ids, but they identify the campaign when a URL
// lost its UTMs, so they are kept and forwarded like the UTMs are.
export const GOOGLE_AUTO_KEYS = Object.freeze(['gad_source', 'gad_campaignid']);

export const CAMPAIGN_PARAM_KEYS = Object.freeze([
  ...UTM_KEYS,
  ...CLICK_ID_KEYS,
  ...GOOGLE_AUTO_KEYS
]);

export const STORAGE_KEY = 'gt_campaign_attribution_v1';

// The routes the Gotrade Indonesia app links serve. adjustSdk.js imports this to pick the GTI vs
// GTG Adjust app for the same pages, so a route missing here tracks its download clicks under the
// wrong app. /sem/ is included because the SEM (paid-search) landing pages are GTI product pages
// (SEOENG-19): without it a GTI SEM page would resolve to the GTG app for web-side click tracking.
export function isGtiPathname(pathname = '') {
  return (
    pathname === '/id' ||
    pathname.startsWith('/id/') ||
    pathname === '/partner' ||
    pathname.startsWith('/partner/') ||
    pathname === '/sem' ||
    pathname.startsWith('/sem/')
  );
}

// Adjust link parameters are part of a URL that also carries og_* fields and a fallback URL; keep
// each value bounded so a mangled ad URL cannot blow the link past what stores and browsers accept.
export const MAX_VALUE_LENGTH = 120;

// Click ids are OPAQUE: Google matches them byte for byte when it imports the conversion, so a
// truncated one is worse than a missing one (it looks healthy in every report and matches nothing).
// They are therefore never truncated -- they are kept whole, or dropped if implausibly long.
export const MAX_CLICK_ID_LENGTH = 512;

const SEM_MEDIUMS = new Set([
  'cpc',
  'ppc',
  'sem',
  'search',
  'paidsearch',
  'paid_search',
  'paid-search',
  'g',
  's'
]);
const GDN_MEDIUMS = new Set(['display', 'gdn', 'banner', 'cpm', 'd']);
const YOUTUBE_MEDIUMS = new Set(['video', 'youtube', 'yt', 'ytv', 'yts', 'vp']);
const GOOGLE_SOURCES = new Set([
  'google',
  'google_ads',
  'googleads',
  'google-ads',
  'adwords',
  'gdn',
  'youtube'
]);

// Campaign-name-ish values: trimmed and truncated (they become Adjust report rows, so a long one
// is a cosmetic problem, not a correctness one).
function cleanValue(value) {
  if (typeof value !== 'string') return '';
  const trimmed = value.trim();
  return trimmed.length > MAX_VALUE_LENGTH ? trimmed.slice(0, MAX_VALUE_LENGTH) : trimmed;
}

// Click ids: kept verbatim, or dropped whole. Never sliced. See MAX_CLICK_ID_LENGTH.
function cleanClickId(value) {
  if (typeof value !== 'string') return '';
  const trimmed = value.trim();
  return trimmed.length > MAX_CLICK_ID_LENGTH ? '' : trimmed;
}

// Google ValueTrack placeholders ({campaignid}, {adgroupid}_{creative}, ...) are substituted by
// Google at click time. A value that still contains braces was NOT expanded -- the URL was pasted
// somewhere ValueTrack does not run. Slugifying it would mint a plausible-looking `sem_campaignid`
// row that silently merges every campaign, so treat it as absent and fall back instead.
function isUnexpandedValueTrack(value) {
  return typeof value === 'string' && /[{}]/.test(value);
}

// Path-shaped values (the adgroup fallback) keep their slashes so they stay comparable with the
// organic hook's adgroup, but everything outside a safe charset is collapsed.
export function sanitizePathForAdjust(value) {
  return cleanValue(value)
    .toLowerCase()
    .replace(/[^a-z0-9._/-]+/g, '-')
    .replace(/-+$/g, '');
}

// Adjust campaign / adgroup / creative names travel as URL params and show up as report rows;
// keep them to a conservative charset so "Brand | Welcome Rewards (ID)" does not become three
// different rows depending on which layer encoded it.
export function slugForAdjust(value) {
  return cleanValue(value)
    .toLowerCase()
    .replace(/[^a-z0-9._-]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

// Reads the campaign parameters out of a query string (or a URLSearchParams / plain object).
// Only the keys in CAMPAIGN_PARAM_KEYS are kept, values are trimmed and bounded, blanks dropped.
export function parseCampaignParams(input) {
  let source;
  if (input instanceof URLSearchParams) {
    source = input;
  } else if (typeof input === 'string') {
    source = new URLSearchParams(input.startsWith('?') ? input.slice(1) : input);
  } else if (input && typeof input === 'object') {
    source = new URLSearchParams(
      Object.entries(input).filter(([, v]) => v !== undefined && v !== null)
    );
  } else {
    return {};
  }

  const params = {};
  for (const key of CAMPAIGN_PARAM_KEYS) {
    const raw = source.get(key);
    const value = CLICK_ID_KEYS.includes(key) ? cleanClickId(raw) : cleanValue(raw);
    if (value && !isUnexpandedValueTrack(value)) params[key] = value;
  }
  return params;
}

export function hasClickId(params) {
  return CLICK_ID_KEYS.some((key) => Boolean(params && params[key]));
}

// True when the visit carries anything that identifies a paid source: a utm_source, a Google
// click id (auto-tagging appends gclid even when someone forgot the UTMs), or gad_campaignid.
export function hasCampaignAttribution(params) {
  return Boolean(params && (params.utm_source || params.gad_campaignid || hasClickId(params)));
}

// SEM / GDN / YouTube from utm_medium (words or ValueTrack {network} codes), with utm_source as a
// tie-breaker; a bare click id classifies as "google"; a non-Google utm_source as "paid".
export function resolveChannel(params) {
  if (!hasCampaignAttribution(params)) return null;

  const medium = (params.utm_medium || '').toLowerCase();
  const source = (params.utm_source || '').toLowerCase();

  if (YOUTUBE_MEDIUMS.has(medium) || source.includes('youtube')) return CHANNEL.YOUTUBE;
  if (GDN_MEDIUMS.has(medium) || source === 'gdn') return CHANNEL.GDN;
  if (SEM_MEDIUMS.has(medium)) return CHANNEL.SEM;

  const isGoogle =
    GOOGLE_SOURCES.has(source) || hasClickId(params) || Boolean(params.gad_campaignid);
  if (isGoogle) return CHANNEL.GOOGLE;
  return source ? CHANNEL.PAID : null;
}

// Builds the query-string suffix Adjust stores as the link's label. Adjust keeps `label` verbatim
// on the link, hands it to the app in the (deferred) deep link, and echoes it in callbacks and raw
// exports, so this is the one place every raw parameter survives end to end. Click ids are NOT in
// here: they are appended to the finished link as top-level params (appendClickIdsToLink) because
// that is the shape Adjust reads them from.
export function buildAttributionLabel(params, { gaClientId = '', path = '', channel = null } = {}) {
  const parts = [];
  if (gaClientId) parts.push(`client_id=${encodeURIComponent(gaClientId)}`);
  const resolvedChannel = channel || resolveChannel(params);
  if (resolvedChannel) parts.push(`channel=${resolvedChannel}`);
  for (const key of [...UTM_KEYS, ...GOOGLE_AUTO_KEYS]) {
    if (params && params[key]) parts.push(`${key}=${encodeURIComponent(params[key])}`);
  }
  if (path) parts.push(`lp=${encodeURIComponent(path)}`);
  return parts.join('&');
}

// The argument set for generateAdjustDeepLink on a paid visit, or null when the visit is organic
// (callers then keep the existing organic behaviour untouched, which is the required fallback).
//
//   campaign : "<channel>_<utm_campaign>" (or "<channel>_<landing slug>" when the ad carried no
//              utm_campaign) so the channel is readable on every Adjust campaign row.
//   adgroup  : utm_content (ad-group / ad detail), else the landing path like the organic hook.
//   creative : utm_term (keyword or placement), else null.
//   channel  : sem | gdn | youtube | google | paid.
//   label    : every raw parameter, see buildAttributionLabel.
//
// The link always rides the website link token: Adjust mints a campaign row the first time a link
// carries a new campaign value, so the channel split needs no Adjust-side setup. Dedicated
// per-channel link tokens would only move paid installs into their own network row; add that when
// it is actually wanted, not before.
export function buildPaidAdjustArgs(params, { gaClientId = '', path = '' } = {}) {
  const channel = resolveChannel(params);
  if (!channel) return null;

  const landingSlug = slugForAdjust(path.replace(/^\/id\//, '').replace(/\/$/, '')) || 'landing';
  const campaignName = slugForAdjust(params.utm_campaign) || landingSlug;

  return {
    isOrganic: false,
    channel,
    campaign: `${channel}_${campaignName}`,
    adgroup: slugForAdjust(params.utm_content) || sanitizePathForAdjust(path),
    creative: slugForAdjust(params.utm_term) || null,
    label: buildAttributionLabel(params, { gaClientId, path, channel })
  };
}

// Appends the Google click identifiers to a generated Adjust link as top-level query parameters
// (the shape Adjust's own Smart Script emits). Adjust passes them through to the app and uses
// gclid / gbraid to hand conversions back to Google Ads; keeping them only inside `label` would
// not do that. Existing params on the link are never touched or duplicated.
export function appendClickIdsToLink(link, params) {
  if (!link || typeof link !== 'string' || !hasClickId(params)) return link || null;

  const hashAt = link.indexOf('#');
  const base = hashAt === -1 ? link : link.slice(0, hashAt);
  const hash = hashAt === -1 ? '' : link.slice(hashAt + 1);
  const query = CLICK_ID_KEYS.filter((key) => params[key])
    .filter((key) => !new RegExp(`[?&]${key}=`).test(base))
    .map((key) => `${key}=${encodeURIComponent(params[key])}`)
    .join('&');

  if (!query) return link;
  const joined = `${base}${base.includes('?') ? '&' : '?'}${query}`;
  return hash ? `${joined}#${hash}` : joined;
}

// Adjust Web SDK callback params for the "download click" event, so the web-side click event
// carries the same channel / campaign / click id as the link the visitor is sent through. This is
// a deliberate SUBSET of CAMPAIGN_PARAM_KEYS: these params ride on every tracked click (Adjust
// gives no error if an over-long set is dropped), and correlating a click needs the channel, the
// campaign and the click id -- ad-level detail (utm_content / utm_term) is already on the link.
export const CALLBACK_PARAM_KEYS = Object.freeze([
  'utm_source',
  'utm_medium',
  'utm_campaign',
  ...CLICK_ID_KEYS,
  'gad_campaignid'
]);

export function buildAttributionCallbackParams(params) {
  if (!hasCampaignAttribution(params)) return [];
  const out = [{ key: 'channel', value: resolveChannel(params) }];
  for (const key of CALLBACK_PARAM_KEYS) {
    if (params[key]) out.push({ key, value: params[key] });
  }
  return out;
}

// --- session persistence -------------------------------------------------------------------
// Gatsby navigates client-side, so a visitor who lands on /id/welcome-rewards-gift/?utm_... and
// taps a CTA on the next page has already lost the query string. First paid touch of the session
// wins; a later paid touch (new ad click in the same tab) replaces it; organic navigation never
// clears it. sessionStorage, not localStorage, so attribution cannot outlive the visit.

function getStorage(storage) {
  if (storage) return storage;
  try {
    return typeof window !== 'undefined' ? window.sessionStorage : null;
  } catch {
    return null;
  }
}

export function persistCampaignParams(params, storage) {
  const store = getStorage(storage);
  if (!store || !hasCampaignAttribution(params)) return false;
  try {
    store.setItem(STORAGE_KEY, JSON.stringify(params));
    return true;
  } catch {
    return false;
  }
}

export function readPersistedCampaignParams(storage) {
  const store = getStorage(storage);
  if (!store) return null;
  try {
    const raw = store.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = parseCampaignParams(JSON.parse(raw));
    return hasCampaignAttribution(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

// The attribution for the current visit: the URL's own parameters when it carries any, else the
// ones persisted earlier in this session, else null (organic).
export function getCampaignAttribution({ search, storage } = {}) {
  let query = search;
  if (query === undefined) {
    query = typeof window !== 'undefined' ? window.location.search : '';
  }
  const fromUrl = parseCampaignParams(query);
  if (hasCampaignAttribution(fromUrl)) {
    persistCampaignParams(fromUrl, storage);
    return fromUrl;
  }
  return readPersistedCampaignParams(storage);
}

// --- Gotrade Ultra (web) links ---------------------------------------------------------------
// USSTOCK-3726. GTU reads utm_* / click ids / src from the URL it is opened with and records them
// against the account on its first sign-in. A plain website click is tagged as the website; a
// visitor who came from an ad keeps the ad's own parameters (the ad is what brought them) and
// gains src=website, so the website hop is still visible.
export const ULTRA_URL = 'https://ultra.heygotrade.com/';

export function buildUltraLink(baseUrl, placement, params) {
  const url = new URL(baseUrl);
  if (hasCampaignAttribution(params)) {
    for (const key of CAMPAIGN_PARAM_KEYS) {
      if (params[key]) url.searchParams.set(key, params[key]);
    }
    url.searchParams.set('src', 'website');
  } else {
    url.searchParams.set('utm_source', 'heygotrade');
    url.searchParams.set('utm_medium', 'website');
    url.searchParams.set('utm_content', placement);
  }
  return url.toString();
}

// Props for an <a> to GTU. The href is static, so SSR, middle-click and copy-link all carry the
// website tag; the click swaps in the visit's ad parameters when there are any (sessionStorage
// only exists in the browser).
export function ultraLinkProps(baseUrl, placement) {
  return {
    href: buildUltraLink(baseUrl, placement, null),
    onClick: (event) => {
      const params = getCampaignAttribution();
      if (params) event.currentTarget.href = buildUltraLink(baseUrl, placement, params);
    }
  };
}
